import * as React from "react";
import styled from "@emotion/styled";
import {
  Button,
  FlexRow,
  Hr,
  Link,
  LoadingSpinner,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { paths } from "utils/paths";
import { ProductWizardStep } from "../ProductWizardStep";
import { ProductDetails } from "../ProductDetails";
import type { StepProps } from "../../types";
import { useUpdateProductDetails } from "../../api/useUpdateProductDetails";
import { useProductQuery } from "../../api/useProductQuery";

function StepConfirmation({ dispatch, state, moveBack }: StepProps) {
  const { mutate, isLoading, isError, error } = useUpdateProductDetails(
    state.product.id,
  );

  const isDraft = state.product.status !== "ACTIVE";

  const moveNext = () => dispatch({ type: "moveNext" });

  const onPublish = () => {
    mutate(
      {
        status: "ACTIVE",
        mediaIDsToDelete: [],
        mediaIDsToSequence: [],
        mediaURLsToAdd: [],
      },
      {
        onSuccess: moveNext,
      },
    );
  };

  const { refetch, isLoading: isRefreshLoading } = useProductQuery(
    state.product.handle,
    [],
  );

  const [isWaiting, setIsWaiting] = React.useState<boolean>(false);

  const onRefresh = () => {
    setIsWaiting(true);
    const timer = setTimeout(() => {
      clearTimeout(timer);
      refetch();
      setIsWaiting(false);
    }, 1000);
  };

  return (
    <ProductWizardStep
      stepTitle="Preview Product Listing"
      state={state}
      moveBack={moveBack}
    >
      <Stack gap="md">
        <Stack gap="xxs">
          {(isRefreshLoading || isWaiting) && <LoadingSpinner />}
          {state.productTemplate.name === "physical item" && (
            <FlexRow flexWrap="wrap" gap="6px 1em" alignItems="flex-start">
              <Text size="xs" style={{ flex: "1 0 420px", margin: 0 }}>
                The preview may take a few moments to update.{" "}
                <TextButton onClick={onRefresh} variant="text">
                  Refresh the preview
                </TextButton>{" "}
                if the information below is not accurate.
              </Text>
              <Button variant="secondary" size="xxs" onClick={onRefresh}>
                Refresh Preview
              </Button>
            </FlexRow>
          )}
        </Stack>
        <ProductDetails
          artistId={state.artistId}
          handle={state.product.handle}
          isPreview
        />

        <Hr />

        {isLoading ? (
          <LoadingSpinner text="Publishing" />
        ) : (
          <FlexRow justifyContent="flex-end" gap="1.5rem">
            {isDraft ? (
              <>
                <Link
                  variant="primary"
                  to={paths.artistShopEdit(state.artistId)}
                >
                  Save As Draft
                </Link>

                <Button onClick={onPublish}>Save and Publish</Button>
              </>
            ) : (
              <Button onClick={moveNext}>Finish</Button>
            )}
          </FlexRow>
        )}

        {isError && <UnexpectedError error={error} />}
      </Stack>
    </ProductWizardStep>
  );
}

StepConfirmation.displayName = "Preview";

const TextButton = styled(Button)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  letterSpacing: 0,
}));
export { StepConfirmation };
